import React, { useState, useEffect, useRef } from "react";
import axios from "./axiosInstance"; // ✅ secured instance
import { getSocket, initiateSocket } from "./socket"; // ✅ use shared socket
import "./styles/GatewaySshManager.css";
import ProtectedComponent from './ProtectedComponent';

const GatewaySshManager = () => {
  const [gwIp, setGwIp] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [terminalOutput, setTerminalOutput] = useState([]);
  const [currentCommand, setCurrentCommand] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [discoveredGateways, setDiscoveredGateways] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [sortField, setSortField] = useState('ip'); // 'ip' or 'mac'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const terminalRef = useRef(null);
  const socketRef = useRef(null);

  const fetchStatus = async () => {
    if (!gwIp) {
      setError("Please enter a Gateway IP");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/gateway/ssh-status", {
        params: { gwIp }
      });
      setStatus(res.data); // ✅ backend always includes sshEnabled
    } catch (err) {
      if (err.response?.status === 403) {
        setError("You must be an admin to access this feature.");
      } else {
        setError("Failed to fetch SSH status");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSsh = async (forceMode = null) => {
    if (!gwIp) {
      setError("Please enter a Gateway IP");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Use forceMode if provided, otherwise toggle based on current state
      const mode = forceMode !== null ? forceMode : (status?.sshEnabled ? 0 : 1);

      const res = await axios.post("/gateway/ssh-toggle", {
        gwIp,
        mode
      });
      setStatus(res.data); // ✅ backend always includes sshEnabled

      // Auto-connect to SSH if it was just enabled
      if (mode === 1 && res.data.sshEnabled) {
        setTimeout(() => connectToSSH(), 2000); // Wait 2 seconds for SSH to be ready
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError("You must be an admin to toggle SSH.");
      } else {
        setError("Failed to toggle SSH");
      }
    } finally {
      setLoading(false);
    }
  };

  const connectToSSH = async () => {
    setTerminalOutput([]);
    addToTerminal("Connecting to SSH...", "system");
    setIsConnected(true);

    // Simulate SSH connection and auto-login
    setTimeout(() => {
      addToTerminal(`root@${gwIp}'s password: `, "system");
    }, 1000);

    setTimeout(() => {
      addToTerminal("●●●●●●●●●", "input");
    }, 1500);

    setTimeout(() => {
      addToTerminal(`Welcome to Newton Gateway ${gwIp}`, "system");
      addToTerminal("Last login: " + new Date().toLocaleString(), "system");
      addToTerminal(`root@newton-${gwIp.split('.').pop()}:~# `, "prompt");
    }, 2500);
  };

  const addToTerminal = (text, type = "output") => {
    setTerminalOutput(prev => [...prev, { text, type, timestamp: Date.now() }]);
  };

  const handleCommandSubmit = async (e) => {
    e.preventDefault();
    if (!currentCommand.trim()) return;

    addToTerminal(`root@newton-${gwIp.split('.').pop()}:~# ${currentCommand}`, "command");

    // Simulate command execution
    setTimeout(() => {
      if (currentCommand.includes("ls")) {
        addToTerminal("bin  boot  dev  etc  home  lib  media  mnt  opt  proc  root  run  sbin  srv  sys  tmp  usr  var", "output");
      } else if (currentCommand.includes("pwd")) {
        addToTerminal("/root", "output");
      } else if (currentCommand.includes("whoami")) {
        addToTerminal("root", "output");
      } else if (currentCommand.includes("uname")) {
        addToTerminal("Linux newton-gateway 4.19.0 #1 SMP armv7l GNU/Linux", "output");
      } else if (currentCommand.includes("exit")) {
        addToTerminal("logout", "system");
        setIsConnected(false);

        // Auto-disable SSH when disconnecting from terminal
        if (gwIp && status?.sshEnabled) {
          addToTerminal("Disabling SSH...", "system");
          // Use a separate async function for the SSH disable call
          const disableSsh = async () => {
            try {
              await axios.post("/gateway/ssh-toggle", {
                gwIp,
                mode: 0
              });
              setStatus(prev => ({ ...prev, sshEnabled: false }));
              addToTerminal("SSH disabled", "system");
            } catch (err) {
              addToTerminal("Failed to disable SSH", "error");
            }
          };
          disableSsh();
        }
        return;
      } else {
        addToTerminal(`bash: ${currentCommand}: command not found`, "error");
      }
      addToTerminal(`root@newton-${gwIp.split('.').pop()}:~# `, "prompt");
    }, 500);

    setCurrentCommand("");
  };

  const discoverGateways = async () => {
    setDiscovering(true);
    setError(null);
    try {
      const res = await axios.get("/gateway/discover");
      setDiscoveredGateways(res.data.gateways || []);

      if (res.data.error) {
        setError(res.data.error);
      }

    } catch (err) {
      if (err.response?.status === 403) {
        setError("You must be an admin to discover gateways.");
      } else {
        setError("Failed to discover gateways");
      }
    } finally {
      setDiscovering(false);
    }
  };

  const selectGateway = (gateway) => {
    setSelectedGateway(gateway);
    setGwIp(gateway.ip);
    setStatus(null);
    setError(null);
  };

  const connectToGateway = async (gateway) => {
    setGwIp(gateway.ip);
    setSelectedGateway(gateway);

    // Check/enable SSH if needed
    if (!gateway.sshEnabled) {
      await toggleSshForGateway(gateway.ip);
    } else {
      // Direct connect if SSH already enabled
      setTimeout(() => connectToSSH(), 1000);
    }
  };

  const toggleSshForGateway = async (ip) => {
    setLoading(true);
    setError(null);

    // Find current SSH state for this gateway
    const currentGateway = discoveredGateways.find(gw => gw.ip === ip);
    const currentSshState = currentGateway?.sshEnabled || false;

    try {
      const res = await axios.post("/gateway/ssh-toggle", {
        gwIp: ip,
        mode: currentSshState ? 0 : 1 // Toggle: disable if enabled, enable if disabled
      });
      setStatus(res.data);

      // Update the gateway in discovered list
      setDiscoveredGateways(prev =>
        prev.map(gw =>
          gw.ip === ip ? { ...gw, sshEnabled: res.data.sshEnabled } : gw
        )
      );

      // Auto-connect if SSH was enabled, disconnect if disabled
      if (res.data.sshEnabled) {
        setGwIp(ip);
        setTimeout(() => connectToSSH(), 2000);
      } else if (isConnected && gwIp === ip) {
        // Disconnect terminal if we're connected to this gateway
        setIsConnected(false);
        setTerminalOutput([]);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError("You must be an admin to toggle SSH.");
      } else {
        setError("Failed to enable SSH");
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize Socket.IO and gateway discovery
  useEffect(() => {
    // Initialize shared Socket.IO connection
    initiateSocket(); // Ensure socket is initialized
    const socket = getSocket(); // Get the shared socket instance
    socketRef.current = socket;

    if (!socket) {
      console.error('Failed to get shared socket instance');
      return;
    }

    // Listen for gateway events from Redis pub/sub
    socket.on('gateway-events', (data) => {
      console.log('Gateway event received:', data);

      if (data.type === 'gateway_list_update') {
        // Update the full gateway list
        setDiscoveredGateways(data.gateways || []);
        setDiscovering(false);
        setError(null);
      } else if (data.type === 'gateway_connected') {
        // Add new gateway to the list
        setDiscoveredGateways(prev => {
          const exists = prev.some(gw => gw.mac === data.gateway.mac);
          if (!exists) {
            return [...prev, data.gateway];
          }
          return prev;
        });
      } else if (data.type === 'gateway_disconnected') {
        // Remove gateway from the list
        setDiscoveredGateways(prev =>
          prev.filter(gw => gw.mac !== data.gateway.mac)
        );
      } else if (data.type === 'gateway_online' || data.type === 'gateway_offline') {
        // Update gateway status
        setDiscoveredGateways(prev =>
          prev.map(gw =>
            gw.mac === data.gateway.mac
              ? { ...gw, online: data.gateway.online }
              : gw
          )
        );
      } else if (data.type === 'gateway_monitor_error') {
        // Handle monitoring errors
        setError(`Gateway monitoring error: ${data.error}`);
        setDiscovering(false);
      }
    });

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Socket.IO connected successfully to:', window.location.origin);
      console.log('Socket ID:', socket.id);
    });

    socket.on('disconnect', () => {
      console.log('Socket.IO disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    socket.on('error', (error) => {
      console.error('Socket.IO error:', error);
    });

    // Initial discovery (fallback if Redis pub/sub doesn't have data yet)
    discoverGateways();

    // Cleanup on unmount
    return () => {
      if (socket) {
        // Remove only the gateway-events listener, don't disconnect shared socket
        socket.off('gateway-events');
      }
    };
  }, []);

  // Auto-scroll terminal to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedGateways = [...discoveredGateways].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';

    // For IP addresses, convert to comparable format
    if (sortField === 'ip') {
      const ipToNumber = (ip) => {
        return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0);
      };
      aVal = ipToNumber(aVal);
      bVal = ipToNumber(bVal);
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  return (
    <ProtectedComponent functionCode="OP07" functionName="Gateway SSH Manager">
      <div className="gateway-ssh-manager">
      {/* Left Panel - Controls */}
      <div className="controls-panel">
        <h2>Newton Gateway SSH Manager</h2>

        {/* Auto-Discovery Section */}
        <div className="discovery-section">
          <div className="discovery-header">
            <h4>Discovered Gateways</h4>
          </div>

          {sortedGateways.length > 0 ? (
            <div className="gateway-table-container">
              <table className="gateway-table">
                <thead>
                  <tr>
                    <th>SSH</th>
                    <th
                      className={`sortable ${sortField === 'ip' ? `sorted-${sortDirection}` : ''}`}
                      onClick={() => handleSort('ip')}
                    >
                      IP Address {sortField === 'ip' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className={`sortable ${sortField === 'mac' ? `sorted-${sortDirection}` : ''}`}
                      onClick={() => handleSort('mac')}
                    >
                      MAC Address {sortField === 'mac' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGateways.map((gateway) => (
                    <tr
                      key={gateway.mac}
                      className={`gateway-row ${selectedGateway?.ip === gateway.ip ? "selected" : ""}`}
                      onClick={() => selectGateway(gateway)}
                    >
                      <td className="status-cell">
                        <label className="ssh-toggle">
                          <input
                            type="checkbox"
                            checked={gateway.sshEnabled || false}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSshForGateway(gateway.ip);
                            }}
                            disabled={loading}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </td>
                      <td className="ip-cell">{gateway.ip}</td>
                      <td className="mac-cell">{gateway.mac}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-gateways">
              {discovering ? "Scanning network..." : "No Newton gateways found"}
            </div>
          )}

          {/* Refresh Button - Under Table */}
          <div className="refresh-container">
            <button
              onClick={discoverGateways}
              disabled={discovering}
              className="btn btn-refresh"
            >
              {discovering ? "⟳" : "🔄"} {discovering ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Manual IP Entry Section */}
        <div className="manual-ip-section manual-gateway-entry">
          <h4>Manual Gateway Entry</h4>
          <div className="ip-input-group">
            <input
              type="text"
              value={gwIp}
              onChange={(e) => setGwIp(e.target.value)}
              placeholder="Enter IP address (e.g., 172.16.0.232)"
              className="ip-input"
            />
            <button
              onClick={() => toggleSsh(1)}
              disabled={loading || !gwIp.trim()}
              className="btn btn-primary btn-check"
            >
              {loading ? "Connecting..." : "Check & Connect"}
            </button>
          </div>

        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Status Display */}
        {status && (
          <div className="status-display">
            <h4>Status</h4>
            <div className="status-item">
              <strong>Gateway:</strong> {status.gateway}
            </div>
          </div>
        )}

        {/* Debug Response */}
        {status?.response && (
          <details className="debug-section">
            <summary className="debug-summary">Debug Response</summary>
            <pre className="debug-response">
              {JSON.stringify(status.response, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* Right Panel - Terminal */}
      {(status?.sshEnabled || isConnected) && (
        <div className="terminal-panel">
          <h3>SSH Terminal - {gwIp}</h3>

          {/* Terminal Window */}
          <div className="terminal-window">
            {/* Terminal Header */}
            <div className="terminal-header">
              <span>root@newton-{gwIp.split('.').pop()} - SSH Terminal</span>
              <div className="terminal-controls">
                <div className="terminal-control red"></div>
                <div className="terminal-control yellow"></div>
                <div className="terminal-control green"></div>
              </div>
            </div>

            {/* Terminal Output */}
            <div ref={terminalRef} className="terminal-output">
              {terminalOutput.map((line, index) => (
                <div key={index} className={`terminal-line ${line.type}`}>
                  {line.text}
                </div>
              ))}
            </div>

            {/* Command Input */}
            <form onSubmit={handleCommandSubmit} className="command-form">
              <input
                type="text"
                value={currentCommand}
                onChange={(e) => setCurrentCommand(e.target.value)}
                placeholder={isConnected ? "type command..." : "connect to SSH first..."}
                className="command-input"
                disabled={!isConnected}
                autoFocus={isConnected}
              />
            </form>
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
};

export default GatewaySshManager;
//
